import { jwtDecode } from 'jwt-decode';
import {makeApiCall} from '../core';
import {getIdToken} from '../auth';
import { Invoice as SharedInvoice, InvoiceLineItem as SharedInvoiceLineItem } from '@ruta/shared';
import type { ItemEnvelope } from '@ruta/shared';

// =====================
// Invoices API
// =====================

// Extended Invoice type with webapp-specific fields
export interface Invoice extends Omit<SharedInvoice, 'lineItems'> {
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  subtotal: number;
  tax: number;
  total: number;
  lineItems: InvoiceLineItem[];
  serviceDate?: string;
  serviceAddress?: string;
  metadata?: {
    serviceTemplateName?: string;
    completedBy?: string;
    duration?: number;
    photos?: string[];
  };
  // Legacy fields for compatibility
  serviceTemplateName?: string;
  visitDetails?: {
    completedAt: string;
    totalServiceTime?: number;
  };
  transactionId?: string;
  processingFees?: {
    totalFee: number;
  };
  paymentError?: string;
}

// Extended InvoiceLineItem type with webapp-specific fields
export interface InvoiceLineItem extends SharedInvoiceLineItem {
  id: string;
  type: 'service' | 'option' | 'labor' | 'service-option' | 'minimum-adjustment' | 'property-metrics';
  quantity: number;
  rate: number;
  details?: string;
}

export interface InvoiceSummary {
  totalCount: number;
  totalAmount: number;
  pendingCount: number;
  pendingAmount: number;
  paidCount: number;
  paidAmount: number;
  failedCount: number;
  failedAmount: number;
}

export interface InvoicesResponse {
  invoices: Invoice[];
  summary: InvoiceSummary;
}

export interface InvoiceFilters {
  status?: string;
  limit?: string;
}

export const getInvoices = async (filters?: InvoiceFilters): Promise<InvoicesResponse> => {
  const params: Record<string, string> = {
    limit: filters?.limit || '50',
  };

  if (filters?.status && filters.status !== 'all') {
    params.status = filters.status;
  }

  const queryParams = new URLSearchParams(params);
  const res = await makeApiCall<ItemEnvelope<InvoicesResponse>>('GET', `/invoices?${queryParams}`);
  return res.data;
};

export const getInvoice = async (invoiceId: string): Promise<{ invoice: Invoice }> => {
  const orgId = orgIdFromToken();
  const path = `/invoices/${invoiceId}${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`;
  const res = await makeApiCall<ItemEnvelope<{ invoice: Invoice }>>('GET', path);
  return res.data;
};

/**
 * Org id from the staff JWT, or undefined when there is no usable token.
 *
 * Unlike the org-scoped routes this must not throw: the invoice endpoint is
 * public, and the unauthenticated fallback below still has to work.
 */
function orgIdFromToken(): string | undefined {
  const token = getIdToken();
  if (!token) return undefined;
  try {
    return jwtDecode<Record<string, string>>(token)['custom:orgId'];
  } catch {
    return undefined;
  }
}

// Fetch invoice data generated from a visit
export const getVisitInvoice = async (visitId: string): Promise<{ invoice: Invoice }> => {
  // The invoice route is public, so `?orgId=` is its only orgId source. With the
  // hint the API reads the visit by primary key; without it, it falls back to a
  // full-table scan. Staff always have the org on their token — pass it.
  const orgId = orgIdFromToken();
  const path = `/visits/${visitId}/invoice${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`;

  // Try authenticated request first
  try {
    const res = await makeApiCall<ItemEnvelope<Invoice>>('GET', path);
    return { invoice: res.data };
  } catch {
    // If authenticated request fails, try public endpoint
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4001';
    const url = `${apiUrl}${path}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: { message: 'Network error' } }));
      throw new Error(errorData.error?.message || `Failed to fetch invoice: ${response.status}`);
    }

    const body: ItemEnvelope<Invoice> = await response.json();
    return { invoice: body.data };
  }
};